import { useParams, useNavigate } from "react-router-dom";
import { EditableField } from "../../../components/editable-field";
import { DateField } from "../../../components/date-field";
import { Button } from "../../../components/button";
import { useEffect, useState } from "react";
import { CompetitionPricesAPI } from "../../../APIs/competition_prices.api";
import { useToast } from "../../../components/toast";
import { CompetitionPrice } from "../../../types/competition.types";
import { Loading } from "../../../components/loading";
import { getObjectDiff } from "../../../utils/object-diff.utils";
import { PaymentTypeTranslations } from "../../../types/payments.types";

export function PriceEditForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const [originalPrice, setOriginalPrice] = useState<CompetitionPrice | null>(
    null
  );
  const [price, setPrice] = useState<CompetitionPrice | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPrice = async () => {
      try {
        const numberId = Number(id);
        if (!id || isNaN(numberId)) {
          navigate("/dashboard/competitions", { replace: true });
          return;
        }

        const response = await CompetitionPricesAPI.getCompetitionPrice(
          numberId
        );
        if (response.statusCode === 200) {
          setPrice(response.data);
          setOriginalPrice(response.data);
        } else {
          addToast({
            type: "error",
            message: response.message,
            location: "bottom-center",
          });
        }
      } catch (err) {
        addToast({
          type: "error",
          message: "Error al obtener el precio",
          location: "bottom-center",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchPrice();
  }, [id, navigate, addToast]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!price) return;

    try {
      const changes = getObjectDiff(originalPrice, price);

      if (!changes) {
        addToast({
          type: "info",
          message: "No se han modificado los datos",
          location: "bottom-center",
          duration: 3000,
        });
        return;
      }

      const response = await CompetitionPricesAPI.updateCompetitionPrice(
        price.id,
        changes
      );

      if (response.statusCode === 200) {
        addToast({
          type: "success",
          message: "Precio actualizado correctamente",
          location: "bottom-center",
        });
        navigate(-1);
      } else {
        addToast({
          type: "error",
          message: response.message,
          location: "bottom-center",
        });
      }
    } catch (error) {
      addToast({
        type: "error",
        message: "Error al actualizar el precio",
        location: "bottom-center",
      });
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLElement>) => {
    const { name, value, type } = e.target as HTMLInputElement;

    setPrice((prev) => {
      if (!prev) return prev;

      return {
        ...prev,
        [name]:
          type === "checkbox"
            ? (e.target as HTMLInputElement).checked
            : type === "number"
            ? parseFloat(value)
            : value,
      };
    });
  };

  if (!price || loading) return <Loading />;

  return (
    <div className="p-8 max-w-2xl mx-auto border border-gray-300 rounded-md">
      <h1 className="text-2xl font-bold mb-6">Editar Precio</h1>

      <form onSubmit={handleSubmit} className="space-y-4">
        <EditableField
          label="Precio"
          name="price"
          type="number"
          value={price.price}
          onChange={handleChange}
          required
        />

        <EditableField
          label="Número de cuotas"
          name="installment_count"
          type="number"
          value={price.installment_count}
          onChange={handleChange}
          required
          min={1}
        />

        <DateField
          label="Fecha de inicio"
          name="start_date"
          value={price.start_date}
          onChange={handleChange}
          required
          type="datetime-local"
        />

        <DateField
          label="Fecha de finalización"
          name="end_date"
          value={price.end_date}
          onChange={handleChange}
          required
          type="datetime-local"
        />

        <EditableField
          label="Tipo de pago"
          name="payment_type"
          type="select"
          value={price.payment_type}
          onChange={handleChange}
          required
          options={Object.entries(PaymentTypeTranslations).map(
            ([key, value]) => ({
              key,
              value,
            })
          )}
          placeholder="Seleccione el tipo de pago"
        />

        <EditableField
          label="Comisión de la plataforma"
          name="platform_fee"
          type="number"
          value={price.platform_fee}
          onChange={handleChange}
        />

        <EditableField
          label="¿Asumir costos de la plataforma de pagos?"
          name="covers_platform_fee"
          type="checkbox"
          value={price.covers_platform_fee}
          onChange={handleChange}
        />

        <div className="flex justify-end gap-4 mt-6">
          <Button
            type="button"
            variant="cancel"
            onClick={() => navigate(-1)}
            text="Cancelar"
          />
          <Button type="submit" variant="edit" text="Guardar Cambios" />
        </div>
      </form>
    </div>
  );
}
